import { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import { todayISO } from '../../utils/dateFormat';
import SearchSelect from '../../components/SearchSelect';
import { confirm } from '../../components/ConfirmDialog';
import { canFiscalRelay } from '../../utils/printService';
import { posCardCharge } from '../../utils/tapxCharge';

export default function Satis() {
    const { user } = useAuthStore();
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
    const [categories, setCategories]   = useState([]);
    const [records, setRecords]         = useState([]);
    const [loading, setLoading]         = useState(true);

    // Category management
    const [catSaving, setCatSaving]     = useState(false);
    const [catModal, setCatModal]       = useState(false);
    const [editingCat, setEditingCat]   = useState(null); // null = create
    const [catForm, setCatForm]         = useState({ name: '', is_open_price: true, price: '', sale_type: 'piece', to_ofis: false });

    // Add sale form — payType: 'nagd' | 'pos'
    // qty: yalnız mürəkkəb kateqoriyada istifadə olunur (avtomatik məbləğ üçün)
    const [form, setForm] = useState({ category_id: '', amount: '', qty: '1', note: '', payType: 'nagd' });
    const [saving, setSaving]           = useState(false);
    // POS kart çəkmə (satış POS ödənişində kartı terminala göndər)
    const [posTerminal, setPosTerminal] = useState(false);
    const [posPay, setPosPay] = useState({ open: false, message: '', closable: false, error: false });
    useEffect(() => { api.get('/orders/pos-terminal').then((r) => setPosTerminal(!!r.data?.pos_terminal)).catch(() => {}); }, []);
    const chargeCard = async (amt, label) => {
        if (!(Number(amt) > 0) || !posTerminal || !canFiscalRelay()) return true;
        const a = Number(amt);
        setPosPay({ open: true, closable: false, error: false, message: `${a.toFixed(2)} ₼ POS terminala göndərilir...` });
        try {
            // SmartOne: 1 addım; Caspos: TapXPhone 2 addım (aç→yoxla→rrn→fiskal satış) — posCardCharge idarə edir
            await posCardCharge(a, label, (m) => setPosPay({ open: true, closable: false, error: false, message: m }));
            setPosPay({ open: true, closable: false, error: false, message: 'Ödəniş qəbul edildi. Yadda saxlanılır...' });
            return true;
        } catch (e) {
            setPosPay({ open: true, closable: true, error: true, message: e.response?.data?.msg || e.message || 'POS çəkiliş baş tutmadı / imtina.' });
            return false;
        }
    };

    // Confirmation popup
    const [confirmPopup, setConfirmPopup] = useState(null); // { payType, amount, categoryName }

    const loadCategories = useCallback(async () => {
        try {
            const res = await api.get('/kassa-ledger/satis-categories');
            setCategories(res.data.data || []);
        } catch { toast.error('Kateqoriyalar yüklənmədi.'); }
    }, []);

    const loadRecords = useCallback(async () => {
        setLoading(true);
        try {
            const today = todayISO();
            const res = await api.get(`/kassa-ledger/satis?from=${today}&to=${today}`);
            setRecords(res.data.data || []);
        } catch { toast.error('Satışlar yüklənmədi.'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadCategories(); }, [loadCategories]);
    useEffect(() => { loadRecords(); }, [loadRecords]);

    const openCatCreate = () => {
        setEditingCat(null);
        setCatForm({ name: '', is_open_price: true, price: '', sale_type: 'piece', to_ofis: false });
        setCatModal(true);
    };
    const openCatEdit = (cat) => {
        setEditingCat(cat);
        setCatForm({
            name: cat.name || '',
            is_open_price: Boolean(Number(cat.is_open_price ?? 1)),
            price: String(cat.price ?? ''),
            sale_type: cat.sale_type || 'piece',
            to_ofis: Boolean(Number(cat.to_ofis ?? 0)),
        });
        setCatModal(true);
    };
    const submitCat = async (e) => {
        e.preventDefault();
        if (!catForm.name.trim()) return toast.error('Ad daxil edin.');
        if (!catForm.is_open_price && (!catForm.price || Number(catForm.price) <= 0)) {
            return toast.error('Mürəkkəb satış üçün qiymət daxil edin.');
        }
        setCatSaving(true);
        try {
            const payload = {
                name: catForm.name.trim(),
                is_open_price: catForm.is_open_price ? 1 : 0,
                price: catForm.is_open_price ? 0 : Number(catForm.price),
                sale_type: catForm.sale_type,
                to_ofis: catForm.to_ofis ? 1 : 0,
            };
            if (editingCat) {
                await api.put(`/kassa-ledger/satis-categories/${editingCat.id}`, payload);
                toast.success('Kateqoriya yeniləndi.');
            } else {
                await api.post('/kassa-ledger/satis-categories', payload);
                toast.success('Kateqoriya əlavə edildi.');
            }
            setCatModal(false);
            loadCategories();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setCatSaving(false); }
    };

    const handleDeleteCategory = async (id) => {
        if (!(await confirm('Bu kateqoriya silinsin?'))) return;
        try {
            await api.delete(`/kassa-ledger/satis-categories/${id}`);
            toast.success('Silindi.');
            loadCategories();
        } catch { toast.error('Silinmədi.'); }
    };

    const handleAddSale = async (e) => {
        e.preventDefault();
        // Kateqoriya seçimi MƏCBURİDİR — seçilmədən satış əlavə edilə bilməz
        if (!form.category_id) return toast.error('Əvvəlcə kateqoriya seçin.');
        // Mürəkkəb kateqoriya halında miqdar × qiymət hesabla
        const selCat = categories.find(c => String(c.id) === String(form.category_id));
        const isComplex = selCat && !Number(selCat.is_open_price ?? 1);
        let amount;
        if (isComplex) {
            const q = Number(form.qty || 0);
            if (!q || q <= 0) return toast.error('Miqdar daxil edin.');
            if (selCat.sale_type === 'piece' && !Number.isInteger(q)) {
                return toast.error('Ədəd ilə satılır — tam say daxil edin.');
            }
            amount = q * Number(selCat.price || 0);
        } else {
            amount = Number(form.amount) || 0;
            if (!amount) return toast.error('Məbləğ daxil edin.');
        }
        const nagd = form.payType === 'nagd' ? amount : 0;
        const pos  = form.payType === 'pos'  ? amount : 0;
        setSaving(true);
        try {
            // POS ödənişdirsə kartı əvvəlcə terminalda çək (imtina → qeyd etmə)
            const charged = await chargeCard(pos, 'Satış');
            if (!charged) { setSaving(false); return; }
            await api.post('/kassa-ledger/satis', {
                category_id: form.category_id || null,
                nagd_amount: nagd,
                pos_amount:  pos,
                note:        form.note || null,
                date:        todayISO(),
            });

            const catName = form.category_id
                ? (categories.find(c => String(c.id) === String(form.category_id))?.name || '')
                : '';

            setPosPay({ open: false, message: '', closable: false, error: false });
            setConfirmPopup({ payType: form.payType, amount, categoryName: catName });
            setForm({ category_id: '', amount: '', qty: '1', note: '', payType: 'nagd' });
            loadRecords();
        } catch (err) { setPosPay({ open: false, message: '', closable: false, error: false }); toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setSaving(false); }
    };

    const handleDeleteRecord = async (id) => {
        if (!(await confirm('Bu qeyd silinsin?'))) return;
        try {
            await api.delete(`/kassa-ledger/satis/${id}`);
            toast.success('Silindi.');
            loadRecords();
        } catch { toast.error('Silinmədi.'); }
    };

    const totalNagd = records.reduce((s, r) => s + Number(r.nagd_amount || 0), 0);
    const totalPos  = records.reduce((s, r) => s + Number(r.pos_amount  || 0), 0);
    const fmt = (v) => Number(v || 0).toFixed(2);
    const inputCls = 'w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400 transition-all';

    return (
        <Layout>
            {/* POS kart ödənişi prosesi — bloklayan popup */}
            {posPay.open && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
                     onClick={(e) => { if (posPay.closable && e.target === e.currentTarget) setPosPay({ open: false, message: '', closable: false, error: false }); }}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl text-center">
                        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center text-3xl mb-4 ${posPay.error ? 'bg-red-100' : 'bg-blue-100'}`}>
                            {posPay.error ? '❌' : <span className="inline-block h-8 w-8 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />}
                        </div>
                        <h3 className="text-base font-bold text-gray-800 mb-1">💳 POS ödənişi</h3>
                        <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{posPay.message}</p>
                        {posPay.closable ? (
                            <button onClick={() => setPosPay({ open: false, message: '', closable: false, error: false })}
                                className="mt-5 w-full py-2.5 rounded-xl text-sm font-bold text-white bg-gray-600 hover:bg-gray-700">Bağla</button>
                        ) : (
                            <p className="mt-4 text-[11px] text-gray-400">Zəhmət olmasa gözləyin — prosesi dayandırmayın.</p>
                        )}
                    </div>
                </div>
            )}
            <div className="max-w-5xl mx-auto p-4 space-y-6">
                <h1 className="text-2xl font-bold text-slate-800">📦 Satış</h1>

                <div className={`grid grid-cols-1 gap-6 ${isAdmin ? 'lg:grid-cols-3' : ''}`}>

                    {/* ── LEFT: Category management (admin only) ────────── */}
                    {isAdmin && (
                        <div className="lg:col-span-1 space-y-4">
                            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="font-bold text-slate-700 text-sm">Kateqoriyalar</h2>
                                    <button
                                        type="button"
                                        onClick={openCatCreate}
                                        className="rounded-xl bg-blue-600 hover:bg-blue-700 px-3 py-1.5 text-xs font-bold text-white"
                                    >
                                        + Yeni
                                    </button>
                                </div>

                                <div className="space-y-1 max-h-72 overflow-y-auto">
                                    {categories.length === 0 && (
                                        <p className="text-xs text-slate-400 text-center py-4">Kateqoriya yoxdur</p>
                                    )}
                                    {categories.map(c => {
                                        const isOpen = Boolean(Number(c.is_open_price ?? 1));
                                        return (
                                            <div key={c.id} className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 rounded-xl">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-slate-700 truncate">{c.name}</div>
                                                    <div className="text-[10px] text-slate-400 mt-0.5">
                                                        {isOpen
                                                            ? '📝 Sadə'
                                                            : `💰 ${Number(c.price || 0).toFixed(2)} ₼ / ${c.sale_type === 'weight' ? 'kq' : c.sale_type === 'liter' ? 'lt' : 'əd'}`}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => openCatEdit(c)}
                                                    className="text-blue-500 hover:text-blue-700 text-xs font-bold"
                                                    title="Redaktə et"
                                                >✎</button>
                                                <button
                                                    onClick={() => handleDeleteCategory(c.id)}
                                                    className="text-red-400 hover:text-red-600 text-xs font-bold"
                                                    title="Sil"
                                                >✕</button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── RIGHT: Sales records ──────────────────────────── */}
                    <div className={isAdmin ? 'lg:col-span-2 space-y-4' : 'space-y-4'}>

                        {/* Add sale form */}
                        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                            <h2 className="font-bold text-slate-700 text-sm mb-3">Satış əlavə et</h2>
                            <form onSubmit={handleAddSale} className="space-y-3">

                                {/* Category */}
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Kateqoriya <span className="text-rose-500">*</span></label>
                                    <SearchSelect
                                        value={form.category_id ? String(form.category_id) : ''}
                                        onChange={(v) => setForm(p => ({ ...p, category_id: v }))}
                                        placeholder="🔍 Kateqoriya axtar və seç..."
                                        options={categories.map(c => ({ value: String(c.id), label: c.name }))}
                                    />
                                </div>

                                {/* Payment type toggle */}
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Ödəniş növü</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setForm(p => ({ ...p, payType: 'nagd' }))}
                                            className={`py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${
                                                form.payType === 'nagd'
                                                    ? 'bg-green-600 border-green-600 text-white shadow-sm'
                                                    : 'bg-white border-gray-200 text-slate-500 hover:border-green-400'
                                            }`}
                                        >
                                            💵 Nağd
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setForm(p => ({ ...p, payType: 'pos' }))}
                                            className={`py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${
                                                form.payType === 'pos'
                                                    ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                                                    : 'bg-white border-gray-200 text-slate-500 hover:border-blue-400'
                                            }`}
                                        >
                                            💳 POS / Kart
                                        </button>
                                    </div>
                                </div>

                                {/* Amount / Qty (mürəkkəb kateqoriyada miqdar avtomatik məbləği hesablayır) */}
                                {(() => {
                                    const selCat = categories.find(c => String(c.id) === String(form.category_id));
                                    const isComplex = selCat && !Number(selCat.is_open_price ?? 1);
                                    const unitLabel = selCat?.sale_type === 'weight' ? 'kq' : selCat?.sale_type === 'liter' ? 'lt' : 'əd';
                                    const stepVal = selCat?.sale_type === 'piece' ? '1' : '0.01';
                                    if (isComplex) {
                                        const autoAmount = (Number(form.qty || 0) * Number(selCat.price || 0));
                                        return (
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">
                                                        Miqdar ({unitLabel})
                                                    </label>
                                                    <input
                                                        type="number" min="0" step={stepVal}
                                                        value={form.qty}
                                                        onChange={e => setForm(p => ({ ...p, qty: e.target.value }))}
                                                        onKeyDown={(e) => {
                                                            // Ədəd ilə olarsa . , ya da ona oxşar onluq simvollar gəlsə blokla
                                                            if (selCat.sale_type === 'piece' && (e.key === '.' || e.key === ',')) e.preventDefault();
                                                        }}
                                                        placeholder={selCat.sale_type === 'piece' ? '1' : '0.5'}
                                                        className={`${inputCls} font-bold text-lg`}
                                                    />
                                                </div>
                                                <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 flex items-center justify-between">
                                                    <span className="text-[11px] text-slate-500">Avtomatik məbləğ:</span>
                                                    <span className={`text-lg font-black font-mono ${form.payType === 'nagd' ? 'text-green-700' : 'text-blue-700'}`}>
                                                        {autoAmount.toFixed(2)} ₼
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div>
                                            <label className={`text-[11px] font-semibold uppercase tracking-wide mb-1 block ${form.payType === 'nagd' ? 'text-green-600' : 'text-blue-600'}`}>
                                                Məbləğ (₼)
                                            </label>
                                            <input
                                                type="number" min="0" step="0.01"
                                                value={form.amount}
                                                onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
                                                placeholder="0.00"
                                                className={`${inputCls} font-bold text-lg ${form.payType === 'nagd' ? 'text-green-700' : 'text-blue-700'}`}
                                            />
                                        </div>
                                    );
                                })()}

                                {/* Note */}
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Qeyd</label>
                                    <input
                                        type="text"
                                        value={form.note}
                                        onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
                                        placeholder="İstəyə bağlı qeyd..."
                                        className={inputCls}
                                    />
                                </div>


                                <button
                                    type="submit"
                                    disabled={saving}
                                    className={`w-full disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-bold transition-all ${
                                        form.payType === 'nagd'
                                            ? 'bg-green-600 hover:bg-green-700'
                                            : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                >
                                    {saving ? 'Saxlanılır...' : `+ ${form.payType === 'nagd' ? 'Nağd' : 'POS'} Satış əlavə et`}
                                </button>
                            </form>
                        </div>

                        {/* Records table */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                                <h2 className="font-bold text-slate-700 text-sm">{todayISO()} — Satışlar</h2>
                                <div className="flex gap-3 text-xs font-bold">
                                    <span className="text-green-700">Nağd: {fmt(totalNagd)} ₼</span>
                                    <span className="text-blue-700">POS: {fmt(totalPos)} ₼</span>
                                </div>
                            </div>

                            {loading ? (
                                <div className="py-10 text-center text-slate-400 text-sm">Yüklənir...</div>
                            ) : records.length === 0 ? (
                                <div className="py-10 text-center text-slate-400 text-sm">Bu tarixdə satış yoxdur.</div>
                            ) : (
                                <div className="overflow-x-auto"><table className="w-full text-sm min-w-[520px]">
                                    <thead>
                                        <tr className="bg-slate-50 text-xs text-slate-400 font-semibold uppercase">
                                            <th className="px-4 py-2 text-left">Kateqoriya</th>
                                            <th className="px-4 py-2 text-right text-green-600">Nağd</th>
                                            <th className="px-4 py-2 text-right text-blue-600">POS</th>
                                            <th className="px-4 py-2 text-left">Qeyd</th>
                                            {isAdmin && <th className="px-4 py-2"></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {records.map(r => (
                                            <tr key={r.id} className="border-t border-slate-50 hover:bg-slate-50">
                                                <td className="px-4 py-3 font-medium text-slate-700">
                                                    {r.category_name || <span className="text-slate-300 italic">—</span>}
                                                </td>
                                                <td className="px-4 py-3 text-right font-bold text-green-700">{Number(r.nagd_amount) > 0 ? fmt(r.nagd_amount) + ' ₼' : '—'}</td>
                                                <td className="px-4 py-3 text-right font-bold text-blue-700">{Number(r.pos_amount) > 0 ? fmt(r.pos_amount) + ' ₼' : '—'}</td>
                                                <td className="px-4 py-3 text-slate-400 text-xs">{r.note || '—'}</td>
                                                {isAdmin && (
                                                    <td className="px-4 py-3 text-right">
                                                        <button
                                                            onClick={() => handleDeleteRecord(r.id)}
                                                            className="text-red-400 hover:text-red-600 text-xs font-bold transition-all"
                                                        >Sil</button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table></div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Confirmation popup ──────────────────────────────────── */}
            {confirmPopup && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-3xl shadow-2xl p-8 w-80 text-center space-y-4 animate-[fadeIn_0.15s_ease]">
                        <div className="text-5xl">
                            {confirmPopup.payType === 'nagd' ? '💵' : '💳'}
                        </div>
                        <div>
                            <p className="text-3xl font-black text-slate-800">
                                {Number(confirmPopup.amount).toFixed(2)} ₼
                            </p>
                            <p className={`text-sm font-bold mt-1 ${confirmPopup.payType === 'nagd' ? 'text-green-600' : 'text-blue-600'}`}>
                                {confirmPopup.payType === 'nagd' ? 'Nağd ödəniş' : 'POS / Kart ödəniş'}
                            </p>
                            {confirmPopup.categoryName && (
                                <p className="text-xs text-slate-400 mt-1">{confirmPopup.categoryName}</p>
                            )}
                        </div>
                        <div className="pt-1 text-xs text-slate-400">Satış uğurla qeyd edildi ✓</div>
                        <button
                            onClick={() => setConfirmPopup(null)}
                            className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-2xl font-bold text-sm transition-all"
                        >
                            Bağla
                        </button>
                    </div>
                </div>
            )}
            {/* KATEQORİYA MODAL — Sadə / Mürəkkəb */}
            {catModal && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setCatModal(false)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                            <h3 className="font-bold text-gray-800">{editingCat ? 'Kateqoriyanı redaktə et' : 'Yeni kateqoriya'}</h3>
                            <button onClick={() => setCatModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <form onSubmit={submitCat} className="p-5 space-y-4">
                            <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Ad</label>
                                <input
                                    type="text" autoFocus
                                    value={catForm.name}
                                    onChange={e => setCatForm(p => ({ ...p, name: e.target.value }))}
                                    placeholder="Məs: Su, Çay, Pivə"
                                    className={inputCls}
                                />
                            </div>

                            <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Satış tipi</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setCatForm(p => ({ ...p, is_open_price: true }))}
                                        className={`rounded-xl border-2 px-3 py-2.5 text-left transition-all ${
                                            catForm.is_open_price ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
                                        }`}
                                    >
                                        <div className={`text-sm font-bold ${catForm.is_open_price ? 'text-blue-700' : 'text-gray-700'}`}>📝 Sadə</div>
                                        <div className="text-[10px] text-gray-500 mt-0.5">Kassir məbləği özü yazır</div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setCatForm(p => ({ ...p, is_open_price: false }))}
                                        className={`rounded-xl border-2 px-3 py-2.5 text-left transition-all ${
                                            !catForm.is_open_price ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
                                        }`}
                                    >
                                        <div className={`text-sm font-bold ${!catForm.is_open_price ? 'text-blue-700' : 'text-gray-700'}`}>💰 Mürəkkəb</div>
                                        <div className="text-[10px] text-gray-500 mt-0.5">Qiymət + əd/kq/lt</div>
                                    </button>
                                </div>
                            </div>

                            {!catForm.is_open_price && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Qiymət (₼)</label>
                                        <input
                                            type="number" step="0.01" min="0"
                                            value={catForm.price}
                                            onChange={e => setCatForm(p => ({ ...p, price: e.target.value }))}
                                            placeholder="0.00"
                                            className={inputCls}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Satış növü</label>
                                        <select
                                            value={catForm.sale_type}
                                            onChange={e => setCatForm(p => ({ ...p, sale_type: e.target.value }))}
                                            className={inputCls}
                                        >
                                            <option value="piece">Ədəd ilə</option>
                                            <option value="weight">Çəki ilə (kq)</option>
                                            <option value="liter">Litr ilə (lt)</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Ofisə köçsün — bu kateqoriyadan nağd satış avtomatik ofisə köçür */}
                            <label className="flex items-center gap-2 rounded-xl border border-teal-200 bg-teal-50/40 p-2.5 text-sm font-semibold text-teal-800 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={Boolean(catForm.to_ofis)}
                                    onChange={e => setCatForm(p => ({ ...p, to_ofis: e.target.checked }))}
                                    className="h-4 w-4 rounded border-gray-300 text-teal-600"
                                />
                                🏦 Ofisə köçsün (nağd kassaya düşür, sonra ofisə)
                            </label>

                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setCatModal(false)}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50">
                                    Ləğv et
                                </button>
                                <button type="submit" disabled={catSaving}
                                    className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold disabled:opacity-50">
                                    {catSaving ? '...' : '💾 Saxla'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
}
